import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, Output } from "@angular/core";
import { CommonModule } from "@angular/common";
import { AnimationLoader, AnimationOptions, LottieComponent, provideLottieOptions } from "ngx-lottie";
import { BehaviorSubject } from "rxjs";
import {
  AssetParamKeys,
  capitalizeFirstLetter,
  CfsContentAsset,
  CfsQrCodeContentAsset,
  CfsTemplateBaseComponent,
  CfsTemplateError,
  CfsTemplateErrorData,
  CfsTemplateErrorTypes,
  ContentSettingKeys,
  ContentSettings,
  generateCfsHtmlTemplateErrorMessage,
  generateCfsTemplateError,
  generateCfsTemplateErrorMessage,
  getAllKeyValueAssetParams,
  getLottieColorByHex,
  getResponsiveAssetsInfo,
  parseQrCodeSettingValue,
  rgbToHex,
  ScreenSizesJsonAsset,
  TemplateAnimationType,
  TemplatePreviewOptions
} from "clearline-common";
import { DigitalAssetGetDto, DigitalAssetScreenSize, KeyValueAsset, KeyValueDto } from "clearline-api";
import { TranslocoService } from "@ngneat/transloco";
import { AnimationItem } from "lottie-web";

type ResponsiveAssetsInfo = {
  [screenSize in DigitalAssetScreenSize]?: DigitalAssetGetDto | null;
};

type AnimationDataSizeAsset = {
  [screenSize in DigitalAssetScreenSize]?: any;
};

type ContentAssetByKeys = {
  [key in AssetParamKeys]: string;
};

@Component({
  selector: "lib-lottie-template",
  templateUrl: "./lottie-template.component.html",
  styleUrls: ["./lottie-template.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, LottieComponent],
  providers: [
    provideLottieOptions({
      player: () => import("lottie-web")
    }),
    AnimationLoader
  ]
})
export class LottieTemplateComponent implements CfsTemplateBaseComponent, AfterViewInit {
  @Input() isPrintView = false;
  @Input() animationType: TemplateAnimationType = TemplateAnimationType.OnDemandAnimated;

  @Input() set screenSize(screenSize: DigitalAssetScreenSize | null) {
    if (this.templateScreenSize && this.templateScreenSize === screenSize) {
      return;
    }

    this.templateScreenSize = screenSize ?? DigitalAssetScreenSize.Full;

    if (this.animationDataSizeAsset[this.templateScreenSize]) {
      // set only if templateOptions is already set
      this.setAnimationToOptions();
    }
  }

  @Input() set options(options: TemplatePreviewOptions | null) {
    if (options) {
      this.setOptions(options);
    }
  }

  @Output() ready = new EventEmitter<void>();
  @Output() error = new EventEmitter<CfsTemplateError>();
  @Output() printReady = new EventEmitter();

  private readonly contentAssetByKeys: ContentAssetByKeys = {
    [AssetParamKeys.MainBackgroundColor]: ContentSettingKeys.BackgroundColor,
    [AssetParamKeys.ButtonBackgroundColor]: ContentSettingKeys.ButtonColor,
    [CfsContentAsset.MainBackgroundColorHex]: "",
    [CfsContentAsset.ButtonBackgroundColorHex]: "",
    [AssetParamKeys.Param1]: "",
    [AssetParamKeys.Param2]: ContentSettingKeys.CampaignSchedule,
    [AssetParamKeys.Param3]: ContentSettingKeys.TemplateCategory,
    [AssetParamKeys.Param4]: "",
    [AssetParamKeys.Param5]: "",
    [AssetParamKeys.Param6]: ""
  };

  private colorSettingKeys = [ContentSettingKeys.BackgroundColor, ContentSettingKeys.ButtonColor];
  private animationDataSizeAsset: AnimationDataSizeAsset = {
    [DigitalAssetScreenSize.Full]: null,
    [DigitalAssetScreenSize.Compact]: null,
    [DigitalAssetScreenSize.Vertical]: null
  };
  private backgroundColor?: string;
  private qrCodeUrl?: string;
  private imageUrl?: string;
  private animationItem: AnimationItem | null = null;

  templateScreenSize: DigitalAssetScreenSize = DigitalAssetScreenSize.Full;
  options$ = new BehaviorSubject<AnimationOptions>(null as any);

  constructor(private element: ElementRef, private translateService: TranslocoService) {}

  ngAfterViewInit(): void {
    this.setBackground();
  }

  play(): void {
    this.playIfAnimated();
  }

  stop(): void {
    this.stopAnimation();
  }

  pause(): void {
    this.stopAnimation();
  }

  restart(): void {
    this.playIfAnimated();
  }

  protected animationCreated(animationItem: AnimationItem): void {
    this.animationItem = animationItem;

    this.ready.emit();

    if (this.animationType === TemplateAnimationType.Animated) {
      this.playAnimation();
    } else if (this.isPrintView || this.animationType === TemplateAnimationType.Static) {
      this.stopAnimation();
    }
  }

  protected domLoaded(): void {
    if (this.isPrintView) {
      const waitingList = [];

      if (this.imageUrl) {
        waitingList.push(fetch(this.imageUrl));
      }

      if (this.qrCodeUrl) {
        waitingList.push(fetch(this.qrCodeUrl));
      }

      Promise.all(waitingList)
        .then(() => {
          this.printReady.emit();
        })
        .catch((error: Error) => this.setError(error));
    }
  }

  private playAnimation(): void {
    if (this.animationItem) {
      this.animationItem.play();
    }
  }

  private stopAnimation(): void {
    if (this.animationItem) {
      this.animationItem.stop();
    }
  }

  private playIfAnimated(): void {
    if (this.animationType === TemplateAnimationType.Animated || this.animationType === TemplateAnimationType.OnDemandAnimated) {
      this.playAnimation();
    }
  }

  private setError(error: Error): void {
    const errorMessage = error.message || generateCfsTemplateErrorMessage(CfsTemplateErrorTypes.ImageLoad);
    const templateError = generateCfsTemplateError(CfsTemplateErrorTypes.ImageLoad, errorMessage);

    this.error.emit(templateError);
  }

  private generateAndSetError(errorType: CfsTemplateErrorTypes, dataField = "", dataValue = ""): void {
    const errorData: CfsTemplateErrorData | null = dataField ? { field: dataField, value: dataValue } : null;
    const message: string = generateCfsHtmlTemplateErrorMessage(errorType);
    const templateError: CfsTemplateError = generateCfsTemplateError(errorType, message, errorData);

    this.error.emit(templateError);
  }

  private setOptions(options: TemplatePreviewOptions): void {
    const { contentSettings, defaultParameters, json, screenSizesJsonAsset } = options;
    this.backgroundColor =
      contentSettings?.backgroundColor || this.parseQrCodeBackgroundColor(defaultParameters) || this.getBackgroundColor(options);
    const assetsInfo: ResponsiveAssetsInfo = getResponsiveAssetsInfo(options);

    this.setBackground();

    this.animationDataSizeAsset = Object.keys(assetsInfo).reduce((acc: ResponsiveAssetsInfo, itemScreenSize, index: number) => {
      const assetItem: DigitalAssetGetDto | null | undefined = assetsInfo[itemScreenSize as DigitalAssetScreenSize];
      const itemDefaultParams: KeyValueDto[] = assetItem?.configuration?.defaultParameters || [];
      const itemJson: string = this.getItemJsonByScreenSize(screenSizesJsonAsset, itemScreenSize as DigitalAssetScreenSize, json);

      return {
        ...acc,
        [itemScreenSize]: this.getAnimationData(itemJson, itemDefaultParams as any, options)
      };
    }, {} as any);

    this.ensureValidScreenSize();
    this.setAnimationToOptions();
  }

  private ensureValidScreenSize(): void {
    if (!this.animationDataSizeAsset[this.templateScreenSize]) {
      this.templateScreenSize = Object.keys(this.animationDataSizeAsset).reduce((acc: DigitalAssetScreenSize, key: string) => {
        if (this.animationDataSizeAsset[key as DigitalAssetScreenSize]) {
          acc = key as DigitalAssetScreenSize;
        }
        return acc;
      }, DigitalAssetScreenSize.Full) as DigitalAssetScreenSize;
    }
  }

  private setBackground(): void {
    if (this.element.nativeElement) {
      const contentWrapperElement: HTMLElement = this.element.nativeElement;

      if (this.backgroundColor) {
        contentWrapperElement.style.backgroundColor = this.backgroundColor;
      }

      if (this.isPrintView) {
        contentWrapperElement.style.backgroundColor = "#fff";
      }
    }
  }

  private getBackgroundColor(options: TemplatePreviewOptions): string {
    const params: KeyValueAsset = getAllKeyValueAssetParams(options, []);

    return Object.keys(params).reduce((acc: string, key: string) => {
      let value: string = params[key];

      if (key === "%%BACKGROUND_COLOR%%" && value) {
        acc = rgbToHex(JSON.parse(value));
      }

      return acc;
    }, "");
  }

  private setAnimationToOptions(): void {
    const animationData = this.animationDataSizeAsset[this.templateScreenSize || DigitalAssetScreenSize.Full];
    const autoplay: boolean = this.animationType === TemplateAnimationType.Animated;
    const lottieOptions: AnimationOptions = { autoplay, loop: true, animationData };

    this.options$.next(lottieOptions);
  }

  private getAnimationData(jsonText: string | undefined, digitalDefaultParams: KeyValueDto[], options: TemplatePreviewOptions): any {
    if (!jsonText) return null;

    const { contentSettings, mediaContent } = options || {};
    const isOldLottie: boolean = !!contentSettings?.displayContent || !!contentSettings?.templateCategory;

    if (mediaContent && !isOldLottie) {
      const allParams: KeyValueAsset = getAllKeyValueAssetParams(options, digitalDefaultParams);

      if (allParams[CfsQrCodeContentAsset.qrCodeUrl]) {
        this.qrCodeUrl = allParams[CfsQrCodeContentAsset.qrCodeUrl];
      }

      if (allParams[CfsQrCodeContentAsset.imageUrl]) {
        this.imageUrl = allParams[CfsQrCodeContentAsset.imageUrl];
      }

      Object.keys(allParams).forEach((key: string) => {
        jsonText = jsonText?.replace(key, allParams[key]);
      });

      return this.getParsedJson(jsonText);
    } else {
      return contentSettings
        ? this.getLottieJson(jsonText, digitalDefaultParams, contentSettings)
        : this.getLottieJsonByAssetParams(jsonText, digitalDefaultParams);
    }
  }

  private getLottieJson(text: string, params: KeyValueDto[] | undefined, settings: ContentSettings): any {
    Object.keys(this.contentAssetByKeys).forEach((key: string) => {
      let value: string = this.tryGetPlaceholder(key, settings);

      if (!value) {
        if (key === AssetParamKeys.Param1) {
          let displayContent: string = capitalizeFirstLetter(settings.displayContent);

          if (!displayContent) displayContent = capitalizeFirstLetter(settings.templateCategory);

          value = displayContent;
        } else {
          value = this.getJsonValueDependOnKey(key, params as KeyValueDto[], settings);
        }
      }

      text = text.replace(key, value.replace(/"/g, '\\"'));
    });

    return this.getParsedJson(text);
  }

  private getLottieJsonByAssetParams(jsonText: string, params: KeyValueDto[] = []): any {
    params?.forEach((item: KeyValueDto) => {
      jsonText = jsonText.replace(item.key, item.value);
    });

    return this.getParsedJson(jsonText);
  }

  private getParsedJson(json = ""): string {
    try {
      return JSON.parse(json);
    } catch (error) {
      console.error("Failed to parse JSON:", error);
      this.error.emit(generateCfsTemplateError(CfsTemplateErrorTypes.JsonParse, "Invalid JSON format"));
      return "";
    }
  }

  private tryGetPlaceholder(key: string, settings: ContentSettings) {
    if (key === AssetParamKeys.Param1 && !settings.displayContent)
      return this.translateService.translateObject("templates.templateTopPlaceholder" + (settings.isPredefined ? "Select" : "Enter"));
    if (key === AssetParamKeys.Param3 && !settings.templateCategory)
      return this.translateService.translateObject("templates.templateButtonPlaceholder");
    if (key === AssetParamKeys.Param2 && !settings.campaignSchedule)
      return this.translateService.translateObject("templates.campaignSchedule" + (settings.isPredefined ? "Select" : "Enter"));
    return null;
  }

  private getJsonValueDependOnKey(key: string, params: KeyValueDto[], settings: ContentSettings): string {
    let value: string;
    const contentSettingKey = this.contentAssetByKeys[key as AssetParamKeys];
    const valueBySettings: string = contentSettingKey ? (settings as any)[contentSettingKey] : null;
    value = valueBySettings ? valueBySettings : (this.getContentDefaultValue(params, key) as string);

    if (valueBySettings && contentSettingKey === ContentSettingKeys.CampaignSchedule) {
      value = value.toUpperCase();
    } else if (valueBySettings && this.colorSettingKeys.some((colorKey: string) => colorKey === contentSettingKey)) {
      value = getLottieColorByHex(value);
    } else {
      value = capitalizeFirstLetter(value);
    }

    return value;
  }

  private getContentDefaultValue(assetParams: KeyValueDto[] | undefined, key: string): string {
    return assetParams?.find((item: KeyValueDto) => item.key === key)?.value || "";
  }

  private getItemJsonByScreenSize(
    screenSizesJsonAsset?: ScreenSizesJsonAsset,
    itemScreenSize?: DigitalAssetScreenSize,
    json?: string
  ): string {
    let result = "";

    if (screenSizesJsonAsset) {
      result = screenSizesJsonAsset[itemScreenSize as DigitalAssetScreenSize] || "";
    } else if (itemScreenSize === DigitalAssetScreenSize.Full) {
      result = json || "";
    }

    return result;
  }

  private parseQrCodeBackgroundColor(defaultParameters?: KeyValueAsset): string {
    if (defaultParameters && defaultParameters[CfsQrCodeContentAsset.backgroundColor]) {
      return parseQrCodeSettingValue("backgroundColor", defaultParameters[CfsQrCodeContentAsset.backgroundColor]) as string;
    }
    return "";
  }
}
